import { type Ref, onMounted, onBeforeUnmount } from 'vue';

import { analyticsEventsLib } from '@/shared/lib';

const SCROLL_DEPTHS = [25, 50, 75, 100] as const;

const getScrollDepthPercent = (el: HTMLElement) => {
  const { scrollTop, scrollHeight, clientHeight } = el;
  if (scrollHeight <= 0) {
    return 100;
  }
  return Math.round(((scrollTop + clientHeight) / scrollHeight) * 100);
};

const getScrolledBlocks = (el: HTMLElement, depthPercent: number): string[] => {
  const cutoff = (depthPercent / 100) * el.scrollHeight;
  const sectionTop = el.getBoundingClientRect().top;
  const blocks = el.querySelectorAll<HTMLElement>('[data-scroll-block]');
  const result: string[] = [];

  for (const block of blocks) {
    const blockTop =
      block.getBoundingClientRect().top - sectionTop + el.scrollTop;
    if (blockTop < cutoff) {
      const name = block.dataset.scrollBlock;
      if (name) result.push(name);
    }
  }

  return result;
};

export function useMainPageScroll(scrollSection: Ref<HTMLElement | null>) {
  const trackedDepths = new Set<number>();

  const handleScroll = () => {
    const el = scrollSection.value;
    if (!el) {
      return;
    }

    const percent = getScrollDepthPercent(el);

    for (const depth of SCROLL_DEPTHS) {
      if (percent >= depth && !trackedDepths.has(depth)) {
        trackedDepths.add(depth);
        analyticsEventsLib.mainScroll({
          scroll_depth: depth,
          scroll_blocks: getScrolledBlocks(el, depth),
        });
      }
    }
  };

  onMounted(() => {
    scrollSection.value?.addEventListener('scroll', handleScroll, {
      passive: true,
    });
  });

  onBeforeUnmount(() => {
    scrollSection.value?.removeEventListener('scroll', handleScroll);
  });
}
